import React, { useEffect } from 'react';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import Cover from '../components/paper/Cover';
import Rich, { PaperBase, smart } from '../components/paper/Rich';
import { FigLabel, MoneyFigure } from '../components/paper/Figures';
import { ArrowDown, ArrowRight } from '../components/Icons';
import useReveal from '../hooks/useReveal';
import { PAPER } from '../content/whitepaper';
import { formatReadingDate, latestReading } from '../content/reading';

const pad = (n) => String(n).padStart(2, '0');

const HOW = [
  [
    'Find strategies',
    'We train agents that hill-climb, improving round after round, in reinforcement-learning environments built for financial markets, until they find trading strategies.',
  ],
  ['Build the systems', 'We create our own agent harnesses and foundation models.'],
  ['Model the markets', 'We design our own time-series models and world models for financial markets.'],
];

const PROCESS_STEPS = [
  ['Research', 'Agents search the markets for forecasts, ideas, and strategies worth a closer look.', 'Ideas to review', false],
  ['Review', 'Every idea is checked against rules set in advance. No model approves its own trade.', 'Approved, declined, or paper-traded', true],
  ['Trade', 'What we approve runs small, live or on paper, inside limits that stay fixed.', 'Orders and positions', false],
  ['Score', 'The market shows what happened, including a sample of the ideas we declined.', 'A record of results', false],
  ['Improve', 'Those results retire weak strategies, refine the ones that work, and sharpen the next round of research.', 'Stronger strategies', false],
];

const PROCESS_RING = PROCESS_STEPS.map(([step]) => step);

function useInitialHash() {
  useEffect(() => {
    const id = decodeURIComponent(window.location.hash.slice(1));
    const target = id && document.getElementById(id);
    if (target) target.scrollIntoView({ behavior: 'instant', block: 'start' });
  }, []);
}

function Title({ text }) {
  return smart(text)
    .split(/(\S+-\S+)/)
    .map((part, i) =>
      i % 2 ? (
        <span className="hm-head__compound" key={i}>
          {part}
        </span>
      ) : (
        part
      ),
    );
}

function Head({ n, title, deck }) {
  return (
    <header className="hm-head">
      <p className="hm-head__num" data-reveal="fade">
        <span className="pp-sec">§ {n}</span>
      </p>
      <h2 className="hm-head__title" data-reveal>
        <Title text={title} />
      </h2>
      {deck ? (
        <p className="hm-head__deck deck" data-reveal style={{ '--reveal-delay': '80ms' }}>
          <Rich text={deck} />
        </p>
      ) : null}
    </header>
  );
}

function WhatWeDo() {
  return (
    <section className="section hm" id="what-we-do" data-nav-tone="light">
      <div className="container">
        <Head n="01" title="What we do" />
        <div className="hm-body">
          <p className="hm-statement" data-reveal>
            A quant neolab focused on research and trading operations.
          </p>
          <div className="hm-practice" data-reveal>
            <p className="pp-label">How we do it</p>
            <ol>
              {HOW.map(([head, body], i) => (
                <li key={head}>
                  <span className="hm-practice__n">{pad(i + 1)}</span>
                  <span className="hm-practice__head">{head}</span>
                  <span className="hm-practice__body">{body}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}

function Process() {
  return (
    <section className="section hm" id="process" data-nav-tone="light">
      <div className="container">
        <Head
          n="02"
          title="The process"
          deck="Research becomes a portfolio. The results make the next round of research better."
        />
        <div className="hm-body">
          <figure className="pp-fig hm-wide" data-reveal>
            <FigLabel n="01" label="The process" />
            <div className="pp-fig__art">
              <div className="fig-loop hm-loop">
                <ol className="fig-loop__steps">
                  {PROCESS_STEPS.map(([step, happens, result, gate], i) => (
                    <li key={step} className={`fig-box${gate ? ' fig-box--gate' : ''}`}>
                      <span className="fig-box__n">{pad(i + 1)}</span>
                      <span className="fig-box__name">{step}</span>
                      <span className="hm-loop__happens">{happens}</span>
                      <span className="fig-box__out">
                        <span className="pp-label">Result</span>
                        {result}
                      </span>
                    </li>
                  ))}
                </ol>
                <div className="fig-loop__return" aria-hidden="true">
                  <i />
                  <i />
                  <i />
                </div>
              </div>
            </div>
          </figure>
          <div className="hm-cols">
            <p className="pp-p" data-reveal>
              <Rich text="**What we measure.** A forecast is checked against what actually happened. A strategy is judged on results after it starts, over a window chosen beforehand. A trade is judged on profit and loss after costs. We set the questions, so a model cannot pick the easy ones." />
            </p>
            <p className="pp-p" data-reveal style={{ '--reveal-delay': '100ms' }}>
              <Rich text="**How research becomes a portfolio.** A better forecast is not a profit on its own. Forecasts become signals, signals become strategies, each strategy is reviewed, and what passes goes into a portfolio: on paper first, then small and live. We score every stage. If a better forecast never shows up in returns after costs, it does not count." />
            </p>
          </div>
          <figure className="pp-fig pp-fig--money hm-wide" data-reveal>
            <FigLabel n="02" label="From research to a portfolio" />
            <div className="pp-fig__art">
              <MoneyFigure
                stages={['Forecasts', 'Signals', 'Strategies', 'Review', 'Portfolio']}
                gate="Review"
                mark="Scored"
              />
            </div>
          </figure>
        </div>
      </div>
    </section>
  );
}

function Reading() {
  return (
    <section className="section hm-read" id="white-paper" data-nav-tone="light">
      <div className="container">
        <header className="hm-read__head">
          <p className="eyebrow">Reading</p>
          <a className="hm-read__index" href="/research/">
            Index
          </a>
        </header>
        <ol className="hm-read__list">
          {latestReading.map(({ kind, date, title, deck, href, pdf }) => (
            <li key={href} className="hm-read__item">
              <p className="hm-read__meta">
                <span>{kind}</span>
                <time dateTime={date}>{formatReadingDate(date)}</time>
              </p>
              <div className="hm-read__main">
                <h2 className="hm-read__title">
                  <a href={href}>{title}</a>
                </h2>
                <p className="hm-read__deck">{deck}</p>
              </div>
              {pdf ? (
                <a className="hm-read__file" href={pdf} download>
                  PDF
                </a>
              ) : (
                <span className="hm-read__file" aria-hidden="true" />
              )}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

export default function HomePage() {
  useReveal();
  useInitialHash();

  return (
    <PaperBase.Provider value="/whitepaper/">
      <a className="skip" href="#main">Skip to content</a>
      <Nav />
      <main id="main">
        <Cover
          eyebrow="Quantitative Super Intelligence Labs"
          title={PAPER.title}
          subtitle="A quant neolab solving RSI for financial markets."
          lead="We research financial markets and run the trading that research produces."
          ringSteps={PROCESS_RING}
          guides={false}
          actions={
            <>
              <a className="btn btn--arrow" href="/whitepaper/">
                Read the white paper
                <ArrowRight />
              </a>
              <a className="btn btn--ghost btn--download" href={PAPER.pdf} download>
                Download PDF
                <ArrowDown />
              </a>
            </>
          }
        />
        <WhatWeDo />
        <Process />
        <Reading />
      </main>
      <Footer />
    </PaperBase.Provider>
  );
}
